import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { useHotelExperiences } from 'app/hooks/data/core/extranet/hotel/useHotelExperiences';
import { useHotelPacks } from 'app/hooks/data/core/extranet/hotel/useHotelPacks';
import { useHotelRooms } from 'app/hooks/data/core/extranet/hotel/useHotelRooms';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type {
  ExtranetHotelOldPack,
  ExtranetHotelPack,
} from 'app/typings/core/extranet/hotel/packs';
import { ExtranetPackStatus } from 'app/typings/core/extranet/hotel/packs';
import type { ExtranetHotelRoom } from 'app/typings/core/extranet/hotel/rooms';
import { ExtranetRoomStatus } from 'app/typings/core/extranet/hotel/rooms';

import { HotelContext } from './HotelContext';

type ModernHotelContextType = {
  experiencesList: ExtranetHotelExperience[];
  shownInInventoryExperiencesList: ExtranetHotelExperience[];
  originalsList: ExtranetHotelExperience[];
  packsList: ExtranetHotelPack[];
  oldPacksList: ExtranetHotelOldPack[];
  roomsList: ExtranetHotelRoom[];
  roomsFromPublishedPacks?: ExtranetHotelRoom[];
  getExperience: (experienceId?: number) => ExtranetHotelExperience | undefined;
  getOriginal: (experienceId?: number) => ExtranetHotelExperience | undefined;
  getRoom: (roomId?: number) => ExtranetHotelRoom | undefined;
  getPack: (packId: number) => ExtranetHotelPack | undefined;
  getPacksContainingRoom: (roomId?: number) => ExtranetHotelPack[];
  getPublishedAndUnpublishedPacksContainingRoom: (
    roomId?: number
  ) => ExtranetHotelPack[];
  hasPublishedPacks: boolean;
  publishedRooms: ExtranetHotelRoom[];
  publishedPacks: ExtranetHotelPack[];
  isLoading: boolean;
};

const defaultModernHotelContext: ModernHotelContextType = {
  experiencesList: [],
  shownInInventoryExperiencesList: [],
  originalsList: [],
  packsList: [],
  oldPacksList: [],
  roomsList: [],
  roomsFromPublishedPacks: [],
  getExperience: () => undefined,
  getOriginal: () => undefined,
  getRoom: () => undefined,
  getPack: () => undefined,
  getPacksContainingRoom: () => [],
  getPublishedAndUnpublishedPacksContainingRoom: () => [],
  hasPublishedPacks: false,
  publishedRooms: [],
  publishedPacks: [],
  isLoading: true,
};

export const ModernHotelContext = createContext<ModernHotelContextType>(
  defaultModernHotelContext
);

type ModernHotelContextProviderProps = {
  children: ReactNode;
};

export const ModernHotelContextProvider = ({
  children,
}: ModernHotelContextProviderProps) => {
  const { hotelId } = useContext(HotelContext);

  const { data: packsList, isLoading: isPacksLoading } = useHotelPacks(hotelId);
  const { data: roomsList, isLoading: isRoomsLoading } = useHotelRooms(hotelId);
  const { data: hotelExperiencesData, isLoading: isExperiencesLoading } =
    useHotelExperiences(hotelId);

  const { experiences, originals } = hotelExperiencesData ?? {};

  const shownInInventoryExperiencesList = experiences?.filter(
    (exp) => exp.showInInventory
  );

  const isLoading = isPacksLoading || isRoomsLoading || isExperiencesLoading;

  const experiencesByPack = packsList?.packs.reduce((acc, pack) => {
    acc[pack.id] =
      experiences?.filter((exp) =>
        pack?.packExperiences
          .map((packExperience) => packExperience.experienceId)
          .includes(exp.id)
      ) || [];

    return acc;
  }, {} as Record<number, ExtranetHotelExperience[]>);

  const roomsByPack = packsList?.packs.reduce((acc, pack) => {
    acc[pack.id] =
      roomsList?.filter((room) =>
        pack.packRooms.map((packRoom) => packRoom.roomId).includes(room.id)
      ) || [];

    return acc;
  }, {} as Record<number, ExtranetHotelRoom[]>);

  const getExperiencesForPack = (packId: number): ExtranetHotelExperience[] => {
    return experiencesByPack?.[packId] ?? [];
  };

  const getRoomsForPack = (packId?: number): ExtranetHotelRoom[] => {
    if (!packId) {
      return [];
    }

    return roomsByPack?.[packId] ?? [];
  };

  const packs =
    packsList?.packs.map((pack) => ({
      ...pack,
      roomIds: undefined,
      experienceIds: undefined,
      rooms: getRoomsForPack(pack.id),
      experiences: getExperiencesForPack(pack.id),
    })) ?? [];

  const publishedPacks =
    packs.filter((pack) => pack.status === 'PUBLISHED') ?? [];

  const publishedRooms =
    roomsList?.filter((room) => room.status === ExtranetRoomStatus.PUBLISHED) ??
    [];

  const roomsFromPublishedPacks = publishedRooms?.filter((room) =>
    publishedPacks.some((pack) =>
      pack.rooms.find((publishedRoom) => publishedRoom.id === room.id)
    )
  );

  const getPack = (packId: number) => {
    if (!packsList) {
      return undefined;
    }

    return packsList.packs?.find((pack) => pack.id === packId);
  };

  const getPacksContainingRoom = (roomId?: number): ExtranetHotelPack[] => {
    if (!roomId) {
      return [];
    }

    return packs.filter((pack) =>
      (pack.packRooms ?? []).some((packRoom) => packRoom.roomId === roomId)
    );
  };

  const getPublishedAndUnpublishedPacksContainingRoom = (
    roomId?: number
  ): ExtranetHotelPack[] => {
    return getPacksContainingRoom(roomId).filter((pack) =>
      [ExtranetPackStatus.PUBLISHED, ExtranetPackStatus.UNPUBLISHED].includes(
        pack.status
      )
    );
  };

  const getExperience = (experienceId?: number) => {
    if (!experiences || !experienceId) {
      return undefined;
    }

    return experiences?.find((exp) => exp.id === experienceId);
  };

  const getOriginal = (experienceId?: number) => {
    if (!originals || !experienceId) {
      return undefined;
    }

    return originals?.find((exp) => exp.id === experienceId);
  };

  const getRoom = (roomId?: number) => {
    if (!roomsList || !roomId) {
      return undefined;
    }

    return roomsList?.find((room) => room.id === roomId);
  };

  return (
    <ModernHotelContext.Provider
      value={{
        experiencesList:
          experiences?.sort(
            (a, b) =>
              (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
          ) ?? [],
        shownInInventoryExperiencesList: shownInInventoryExperiencesList ?? [],
        originalsList: originals ?? [],
        packsList: packsList?.packs ?? [],
        oldPacksList: packsList?.oldPacks ?? [],
        getPacksContainingRoom,
        getPublishedAndUnpublishedPacksContainingRoom,
        hasPublishedPacks: publishedPacks.length > 0,
        publishedPacks,
        roomsList:
          roomsList?.sort(
            (a, b) =>
              (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
          ) ?? [],
        roomsFromPublishedPacks,
        getPack,
        getExperience,
        getOriginal,
        getRoom,
        publishedRooms,
        isLoading,
      }}
    >
      {children}
    </ModernHotelContext.Provider>
  );
};
